import React, { useState, useMemo } from 'react';
import { PetType } from '@spike/model';
import { Service, ServicePricing, FixedPriceOption } from 'model/Service';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { buildPricingTaxesFromMarketplaces } from 'components/Service/Pricing/utils/taxElements';
import styles from './WalkingPricingStep.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { WalkingTaxesSelector } from './Sections/Taxes/WalkingTaxesSelector';
import { WalkingPriceField } from './Sections/PriceFields/WalkingPriceField';

interface WalkingPricingStepProps {
    selectedPetTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    existingService?: Service;
    onNext: (pricing: ServicePricing, marketplaces: Service['marketplaces']) => void;
}

export const WalkingPricingStep: React.FC<WalkingPricingStepProps> = ({
    selectedPetTypes,
    marketplaces: initialMarketplaces,
    existingService,
    onNext
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();

    const [taxable, setTaxable] = useState(
        () =>
            existingService?.pricing?.taxable ||
            initialMarketplaces.some(marketplace => marketplace.taxes.length > 0) ||
            false
    );
    const [marketplaces, setMarketplaces] = useState<Service['marketplaces']>(() => initialMarketplaces);

    const [price, setPrice] = useState<number>(() => {
        const firstPetPrice = existingService?.pricing?.fixedPrice?.[0];
        return firstPetPrice?.price || 0;
    });

    const [priceError, setPriceError] = useState<string | null>(null);

    React.useEffect(() => {
        setMarketplaces(prev =>
            initialMarketplaces.map(marketplace => {
                const existing = prev.find(mp => mp.marketplaceId === marketplace.marketplaceId);
                return {
                    ...marketplace,
                    taxes: existing?.taxes ?? marketplace.taxes,
                    staffIds: existing?.staffIds ?? marketplace.staffIds,
                    roomIds: marketplace.roomIds ?? existing?.roomIds ?? [],
                    roomUnitIds: marketplace.roomUnitIds ?? existing?.roomUnitIds ?? [],
                    vehicleIds: marketplace.vehicleIds ?? existing?.vehicleIds ?? []
                };
            })
        );
    }, [initialMarketplaces]);

    const taxes = useMemo(
        () => buildPricingTaxesFromMarketplaces(marketplaces, organizationServicesInfo),
        [marketplaces, organizationServicesInfo]
    );

    const isValid = useMemo(() => {
        if (!(price > 0) || !!priceError) {
            return false;
        }

        if (taxable && taxes.length === 0) {
            return false;
        }

        return true;
    }, [price, priceError, taxable, taxes]);

    const handleNext = () => {
        if (!isValid) {
            return;
        }

        const fixedPriceArray: Array<FixedPriceOption> = selectedPetTypes.map(petType => ({
            petType,
            price: price
        }));

        const pricing: ServicePricing = {
            taxable,
            taxes,
            fixedPrice: fixedPriceArray
        };

        onNext(pricing, marketplaces);
    };

    return (
        <div className={styles.container}>
            <div className={styles.sectionsContainer}>
                <WalkingPriceField
                    price={price}
                    onPriceChange={setPrice}
                    onErrorChange={setPriceError}
                />

                <WalkingTaxesSelector
                    taxable={taxable}
                    marketplaces={marketplaces}
                    onTaxableChange={setTaxable}
                    onChangeMarketplaces={setMarketplaces}
                />
            </div>

            <div className={styles.actions}>
                <Button
                    label="Next"
                    onClick={handleNext}
                    disabled={!isValid}
                    size="large"
                    variant="primary"
                />
            </div>
        </div>
    );
};
